import { Link, NavLink, useLocation } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { LayoutDashboard, Plus, Receipt, Truck, Users, Trash2, Building2, Settings as SettingsIcon } from "lucide-react";
import { ViewingBanner } from "@/components/owner/ViewingBanner";
import { useMe } from "@/lib/useMe";
import { Shield } from "lucide-react";

const navItems = [
  { to: "/admin", end: true, label: "Dashboard", short: "Loads", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/admin/loads/new", label: "Create Load", short: "New", icon: Plus, testId: "nav-create-load" },
  { to: "/admin/invoices", label: "Invoices", icon: Receipt, testId: "nav-invoices" },
  { to: "/admin/brokers", label: "Brokers", icon: Building2, testId: "nav-brokers" },
  { to: "/admin/drivers", label: "Drivers", icon: Users, testId: "nav-drivers" },
  { to: "/admin/trash", label: "Trash", icon: Trash2, testId: "nav-trash" },
];

export default function AdminLayout({ children }) {
  const loc = useLocation();
  const { me } = useMe();
  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="admin-layout">
      <aside className="hidden md:flex md:w-64 flex-col border-r border-slate-200 bg-white">
        <Link to="/admin" className="h-16 px-6 flex items-center gap-2 border-b border-slate-200">
          <div className="w-9 h-9 rounded-xl bg-[#002FA7] grid place-items-center text-white">
            <Truck className="w-5 h-5" strokeWidth={2.4} />
          </div>
          <span className="font-bold text-lg tracking-tight" style={{ fontFamily: "Outfit" }}>
            Fleet Sheet
          </span>
        </Link>
        <nav className="p-3 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#002FA7] text-white"
                    : "text-slate-700 hover:bg-slate-100"
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto p-4 border-t border-slate-200 text-xs text-slate-500">
          Carrier admin workspace
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <ViewingBanner />
        <header className="h-16 border-b border-slate-200 bg-white/80 backdrop-blur-xl flex items-center justify-between px-4 md:px-8 sticky top-0 z-20">
          <div className="md:hidden flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#002FA7] grid place-items-center text-white">
              <Truck className="w-4 h-4" />
            </div>
            <span className="font-bold" style={{ fontFamily: "Outfit" }}>Fleet Sheet</span>
          </div>
          <div className="hidden md:block text-sm text-slate-500">
            {[["/admin/invoices", "Invoices"], ["/admin/brokers", "Brokers"], ["/admin/drivers", "Drivers"], ["/admin/trash", "Trash"], ["/admin/settings", "Settings"]].find(([p]) => loc.pathname.startsWith(p))?.[1] || "Loads"}
          </div>
          <div className="flex items-center gap-3">
            <UserButton afterSignOutUrl="/">
              <UserButton.MenuItems>
                <UserButton.Link label="Settings" labelIcon={<SettingsIcon className="w-4 h-4" />} href="/admin/settings" />
                {me?.is_owner && <UserButton.Link label="Owner console" labelIcon={<Shield className="w-4 h-4" />} href="/owner" />}
                <UserButton.Action label="manageAccount" />
              </UserButton.MenuItems>
            </UserButton>
          </div>
        </header>
        <main className="flex-1 min-w-0 p-4 pb-24 md:p-8">{children}</main>
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 flex overflow-x-auto border-t border-slate-200 bg-white">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`mobile-${item.testId}`}
              className={({ isActive }) =>
                `flex-1 min-w-[56px] flex flex-col items-center gap-1 py-2 text-[11px] ${
                  isActive ? "text-[#002FA7] font-semibold" : "text-slate-500"
                }`
              }
            >
              <item.icon className="w-5 h-5" />
              {item.short || item.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
